import { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  PenTool,
  Download,
  CheckCircle,
  X,
  RotateCcw,
  Eye,
  AlertTriangle,
  Type,
  Maximize2,
  ZoomIn,
  ZoomOut,
  Minimize2,
} from "lucide-react";
import { assignmentService, documentService } from "@/services/documentService";
import { DocumentAssignment, SignatureData } from "@/types/document";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface DocumentSigningModalProps {
  open: boolean;
  assignment: DocumentAssignment;
  onClose: () => void;
  onSuccess: () => void;
}

export const DocumentSigningModal = ({
  open,
  assignment,
  onClose,
  onSuccess,
}: DocumentSigningModalProps) => {
  const [signatureData, setSignatureData] = useState<string>("");
  const [signatureMethod, setSignatureMethod] = useState<"draw" | "type">(
    "draw",
  );
  const [typedSignature, setTypedSignature] = useState<string>("");
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastX, setLastX] = useState(0);
  const [lastY, setLastY] = useState(0);
  const [documentUrl, setDocumentUrl] = useState<string>("");
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  const [previewError, setPreviewError] = useState(false);
  const [showFullscreenPreview, setShowFullscreenPreview] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(100);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Initialize canvas when component mounts
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
  }, [open]);

  // Load document preview when modal opens
  useEffect(() => {
    let currentUrl: string | null = null;

    const loadDocumentPreview = async () => {
      try {
        setIsLoadingPreview(true);
        setPreviewError(false);

        // Clean up previous URL if it exists
        if (currentUrl && currentUrl.startsWith("blob:")) {
          URL.revokeObjectURL(currentUrl);
          currentUrl = null;
        }

        const blob = await documentService.downloadDocument(
          assignment.documentId,
        );
        const url = URL.createObjectURL(blob);
        currentUrl = url;
        setDocumentUrl(url);
      } catch (error) {
        console.error("Failed to load document preview:", error);
        setPreviewError(true);
      } finally {
        setIsLoadingPreview(false);
      }
    };

    if (open && assignment.documentId) {
      // Add a small delay to prevent rapid successive calls
      const timeoutId = setTimeout(() => {
        loadDocumentPreview();
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        // Clean up blob URL when component unmounts or modal closes
        if (currentUrl && currentUrl.startsWith("blob:")) {
          URL.revokeObjectURL(currentUrl);
        }
      };
    }

    return () => {
      // Clean up blob URL when component unmounts or modal closes
      if (currentUrl && currentUrl.startsWith("blob:")) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [open, assignment.documentId]); // Removed documentUrl from dependencies

  const refreshPreview = async () => {
    setIsLoadingPreview(true);
    setPreviewError(false);

    try {
      // Clean up existing URL before creating new one
      if (documentUrl && documentUrl.startsWith("blob:")) {
        URL.revokeObjectURL(documentUrl);
      }

      const blob = await documentService.downloadDocument(
        assignment.documentId,
      );
      const url = URL.createObjectURL(blob);
      setDocumentUrl(url);
    } catch (error) {
      console.error("Failed to refresh document preview:", error);
      setPreviewError(true);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const signMutation = useMutation({
    mutationFn: async (signature: SignatureData) => {
      return assignmentService.signDocument(assignment.id, signature);
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Document signed successfully",
      });

      // Clean up and close modal
      handleCloseModal();
      onSuccess();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to sign document",
        variant: "destructive",
      });
    },
  });

  const startDrawing = (
    e:
      React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>,
  ) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    let x, y;

    if ("touches" in e) {
      // Touch event
      const touch = e.touches[0];
      x = touch.clientX - rect.left;
      y = touch.clientY - rect.top;
    } else {
      // Mouse event
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    setIsDrawing(true);
    setLastX(x);
    setLastY(y);
  };

  const draw = (
    e:
      React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>,
  ) => {
    e.preventDefault();
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let x, y;

    if ("touches" in e) {
      // Touch event
      const touch = e.touches[0];
      x = touch.clientX - rect.left;
      y = touch.clientY - rect.top;
    } else {
      // Mouse event
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";

    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(x, y);
    ctx.stroke();

    setLastX(x);
    setLastY(y);
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);

    // Capture signature data
    const canvas = canvasRef.current;
    if (canvas) {
      const dataURL = canvas.toDataURL("image/png");
      setSignatureData(dataURL);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Clear the canvas and set white background
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setSignatureData("");
  };

  const handleSign = () => {
    if (!signatureData) {
      toast({
        title: "Signature Required",
        description:
          signatureMethod === "draw"
            ? "Please provide your signature before signing the document"
            : "Please enter your full legal name before signing the document",
        variant: "destructive",
      });
      return;
    }

    const signature: SignatureData = {
      signatureType: signatureMethod === "draw" ? "DRAWN" : "TYPED",
      signatureData: signatureData,
      timestamp: new Date().toISOString(),
      ip: "127.0.0.1", // This would come from the server
    };

    signMutation.mutate(signature);
  };

  const downloadDocument = async () => {
    try {
      const blob = await documentService.downloadDocument(
        assignment.documentId,
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${assignment.documentTitle}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download document",
        variant: "destructive",
      });
    }
  };

  const handleCloseModal = () => {
    // Clean up blob URL when modal closes
    if (documentUrl && documentUrl.startsWith("blob:")) {
      URL.revokeObjectURL(documentUrl);
    }

    // Reset all state
    setDocumentUrl("");
    setIsLoadingPreview(true);
    setPreviewError(false);
    setSignatureData("");
    setSignatureMethod("draw");
    setTypedSignature("");
    setShowFullscreenPreview(false);
    setPreviewZoom(100);
    clearSignature();

    onClose();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleCloseModal}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sign Document</DialogTitle>
            <DialogDescription>
              Review and sign the document by providing your digital signature.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Document Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Document Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="font-medium">{assignment.documentTitle}</div>
                </div>

                <div className="flex items-center gap-4">
                  <Badge variant="outline">
                    Assigned:{" "}
                    {format(new Date(assignment.assignedAt), "MMM d, yyyy")}
                  </Badge>
                </div>

                <Button
                  variant="outline"
                  onClick={downloadDocument}
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download & Review Document
                </Button>
              </CardContent>
            </Card>

            {/* Document Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Document Preview
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowFullscreenPreview(true)}
                      disabled={!documentUrl || previewError}
                      className="flex items-center gap-2"
                    >
                      <Maximize2 className="h-4 w-4" />
                      Expand
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingPreview ? (
                  <div className="h-64 bg-muted rounded-lg flex items-center justify-center">
                    <div className="text-center text-muted-foreground">
                      <div>Loading document preview...</div>
                    </div>
                  </div>
                ) : previewError ? (
                  <div className="h-64 bg-muted rounded-lg flex items-center justify-center">
                    <div className="text-center text-muted-foreground">
                      <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-warning" />
                      <div>Failed to load document preview</div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={refreshPreview}
                        className="mt-2"
                      >
                        Try Again
                      </Button>
                    </div>
                  </div>
                ) : documentUrl ? (
                  <div className="space-y-3">
                    <iframe
                      src={documentUrl}
                      className="w-full h-[500px] border rounded-lg"
                      title="Document Preview"
                      onLoad={(e) => {
                        // Additional check to ensure iframe content loaded
                        const iframe = e.target as HTMLIFrameElement;
                        if (iframe.contentDocument || iframe.contentWindow) {
                          setPreviewError(false);
                        }
                      }}
                      onError={(e) => {
                        console.error("Failed to load document in iframe");
                        setPreviewError(true);
                      }}
                      style={{
                        minHeight: "500px",
                        backgroundColor: "#f8f9fa",
                      }}
                    />
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <div>
                        Having trouble viewing?{" "}
                        <button
                          onClick={downloadDocument}
                          className="text-info hover:underline"
                        >
                          Download the document
                        </button>{" "}
                        to view it in your default PDF viewer.
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowFullscreenPreview(true)}
                        className="flex items-center gap-1 text-xs h-7"
                      >
                        <Maximize2 className="h-3 w-3" />
                        Full View
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="h-64 bg-muted rounded-lg flex items-center justify-center">
                    <div className="text-center text-muted-foreground">
                      <div>Document preview not available</div>
                      <div className="text-sm mt-1">
                        Click "Download & Review Document" to view the full
                        document
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Digital Signature */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PenTool className="h-5 w-5" />
                  Digital Signature
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Signature Method Selection */}
                <div className="flex gap-2 mb-4">
                  <Button
                    variant={signatureMethod === "draw" ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setSignatureMethod("draw");
                      setSignatureData("");
                      setTypedSignature("");
                    }}
                    className="flex items-center gap-2"
                  >
                    <PenTool className="h-4 w-4" />
                    Draw Signature
                  </Button>
                  <Button
                    variant={signatureMethod === "type" ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setSignatureMethod("type");
                      setSignatureData("");
                      clearSignature();
                    }}
                    className="flex items-center gap-2"
                  >
                    <Type className="h-4 w-4" />
                    Type Signature
                  </Button>
                </div>

                <div className="text-sm text-muted-foreground">
                  {signatureMethod === "draw"
                    ? "Please sign in the box below using your mouse or touch device. Your signature will be legally binding."
                    : "Please type your full legal name below. Your typed signature will be legally binding."}
                </div>

                {/* Conditional Signature Input */}
                {signatureMethod === "draw" ? (
                  // Draw Signature Section
                  <div className="space-y-4">
                    {/* Signature Canvas */}
                    <div className="border-2 border-dashed border-border rounded-lg p-4">
                      <canvas
                        ref={canvasRef}
                        width={600}
                        height={200}
                        className="border border-border rounded cursor-crosshair w-full"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        style={{ touchAction: "none" }}
                      />
                    </div>
                  </div>
                ) : (
                  // Type Signature Section
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium block mb-2">
                        Type your full legal name
                      </label>
                      <Input
                        value={typedSignature}
                        onChange={(e) => {
                          setTypedSignature(e.target.value);
                          if (e.target.value.trim()) {
                            setSignatureData(`typed:${e.target.value.trim()}`);
                          } else {
                            setSignatureData("");
                          }
                        }}
                        placeholder="Enter your full legal name"
                        className="text-lg"
                      />
                    </div>

                    {/* Signature Preview */}
                    {typedSignature && (
                      <div className="border border-border rounded-lg p-6 bg-muted">
                        <div className="text-center">
                          <div
                            className="text-3xl text-foreground mb-2"
                            style={{
                              fontFamily:
                                "Brush Script MT, cursive, Dancing Script, serif",
                              fontStyle: "italic",
                              fontWeight: "400",
                            }}
                          >
                            {typedSignature}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Signature Preview
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Signature Controls */}
                <div className="flex justify-between items-center">
                  {signatureMethod === "draw" ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearSignature}
                      className="flex items-center gap-2"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Clear Signature
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setTypedSignature("");
                        setSignatureData("");
                      }}
                      className="flex items-center gap-2"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Clear Name
                    </Button>
                  )}

                  {signatureData && (
                    <div className="flex items-center gap-2 text-success">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm">
                        {signatureMethod === "draw"
                          ? "Signature captured"
                          : "Name entered"}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Legal Agreement */}
            <Card className="border-warning/30">
              <CardContent className="pt-4">
                <div className="text-sm">
                  <strong>Legal Agreement:</strong> By signing this document,
                  you acknowledge that:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>You have read and understood the document contents</li>
                    <li>
                      Your digital signature has the same legal effect as a
                      handwritten signature
                    </li>
                    <li>You are authorized to sign this document</li>
                    <li>All information provided is accurate and complete</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={handleCloseModal}
                disabled={signMutation.isPending}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSign}
                disabled={!signatureData || signMutation.isPending}
                className="bg-success hover:bg-success/90"
              >
                {signMutation.isPending ? (
                  "Signing..."
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Sign Document
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Document Preview Modal */}
      <Dialog
        open={showFullscreenPreview}
        onOpenChange={setShowFullscreenPreview}
      >
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                {assignment.documentTitle} - Full Preview
              </div>
              <div className="flex items-center gap-2">
                {/* Zoom Controls */}
                <div className="flex items-center gap-1 border rounded-md p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setPreviewZoom(Math.max(50, previewZoom - 25))
                    }
                    className="h-8 w-8 p-0"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-medium px-2 min-w-[60px] text-center">
                    {previewZoom}%
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setPreviewZoom(Math.min(200, previewZoom + 25))
                    }
                    className="h-8 w-8 p-0"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPreviewZoom(100)}
                  className="text-xs"
                >
                  Reset
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadDocument}
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFullscreenPreview(false)}
                  className="flex items-center gap-2"
                >
                  <Minimize2 className="h-4 w-4" />
                  Close
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            {documentUrl && !previewError ? (
              <iframe
                src={documentUrl}
                className="w-full h-full border rounded-lg"
                title="Fullscreen Document Preview"
                style={{
                  height: "calc(95vh - 120px)",
                  transform: `scale(${previewZoom / 100})`,
                  transformOrigin: "top left",
                  width: `${100 / (previewZoom / 100)}%`,
                  minHeight: "calc(95vh - 120px)",
                }}
                onLoad={(e) => {
                  const iframe = e.target as HTMLIFrameElement;
                  if (iframe.contentDocument || iframe.contentWindow) {
                    setPreviewError(false);
                  }
                }}
                onError={(e) => {
                  console.error("Failed to load document in fullscreen iframe");
                  setPreviewError(true);
                }}
              />
            ) : (
              <div className="h-full bg-muted rounded-lg flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-warning" />
                  <div className="text-lg font-medium">
                    Document Preview Unavailable
                  </div>
                  <div className="text-sm mt-2">
                    Please download the document to view it.
                  </div>
                  <Button
                    variant="outline"
                    onClick={downloadDocument}
                    className="mt-4"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Document
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
